import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { tv } from 'tailwind-variants';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/shared/components/ui';
import type { NavItem } from '../model';

const styles = tv({
  slots: {
    container:
      'flex items-center cursor-pointer justify-between rounded-[1.2rem] gap-[0.8rem] text-secondary medium5 h-[3.6rem] py-[0.8rem] pl-[1rem] pr-[1.2rem] hover:bg-accent hover:text-accent-foreground w-full',
    content: 'flex items-center gap-[0.8rem] min-w-0',
    icon: 'size-[1.6rem] shrink-0',
    rightIcon: 'size-[1.2rem] shrink-0 text-tertiary',
    trailing: 'flex items-center gap-[0.8rem] shrink-0',
    counter:
      'size-[1.6rem] shrink-0 rounded-full bg-destructive text-white font-digits font-semibold text-[1rem] leading-[1.2rem] flex items-center justify-center',
  },
  variants: {
    isActive: {
      true: {
        container: 'bg-accent text-accent-foreground',
      },
    },
    collapsed: {
      true: {
        container: 'justify-center px-0 size-[3.6rem] rounded-[1.2rem]',
        content: 'gap-0',
      },
    },
    disabled: {
      true: {
        container: 'opacity-50 pointer-events-none cursor-default',
      },
    },
  },
});

type Props = Omit<NavItem, 'key' | 'display' | 'activePatterns'> & {
  isActive: boolean;
  collapsed?: boolean;
  onClick?: () => void;
};

const SidebarNavigationItem = ({
  icon,
  label,
  href,
  isActive,
  external,
  rightIcon,
  counter,
  disabled,
  collapsed = false,
  onClick,
}: Props) => {
  const IconComponent = icon;
  const RightIcon = rightIcon;
  const classes = styles({ isActive, collapsed, disabled });

  const content = (
    <>
      <div className={classes.content()}>
        <IconComponent className={classes.icon()} />
        {!collapsed && <span className="truncate">{label}</span>}
      </div>
      {!collapsed && (counter || RightIcon) && (
        <div className={classes.trailing()}>
          {Boolean(counter) && (
            <span className={classes.counter()}>{counter}</span>
          )}
          {RightIcon && <RightIcon className={classes.rightIcon()} />}
        </div>
      )}
    </>
  );

  let element: ReactNode;

  if (disabled) {
    element = (
      <span aria-disabled className={classes.container()}>
        {content}
      </span>
    );
  } else if (external && href === '') {
    element = (
      <button
        type="button"
        onClick={onClick}
        aria-label={collapsed ? label : undefined}
        className={classes.container()}>
        {content}
      </button>
    );
  } else if (external) {
    element = (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onClick}
        aria-label={collapsed ? label : undefined}
        className={classes.container()}>
        {content}
      </a>
    );
  } else if (href === '') {
    element = (
      <button
        type="button"
        onClick={onClick}
        aria-label={collapsed ? label : undefined}
        className={classes.container()}>
        {content}
      </button>
    );
  } else {
    element = (
      <Link
        to={href}
        onClick={onClick}
        aria-label={collapsed ? label : undefined}
        className={classes.container()}>
        {content}
      </Link>
    );
  }

  if (!collapsed || disabled) {
    return element;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{element}</TooltipTrigger>
      <TooltipContent side="right" sideOffset={12} className="headline5">
        {label}
      </TooltipContent>
    </Tooltip>
  );
};

export { SidebarNavigationItem };
